import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import 'leaflet/dist/leaflet.css';
import PhotoGallery from './PhotoGallery';
import PhotoUploadModal from './PhotoUploadModal';

// Component to handle map view updates
function MapUpdater({ center, zoom }) {
  const map = useMap();

  useEffect(() => {
    if (center && zoom) {
      map.setView(center, zoom, { animate: true });
    }
  }, [center, zoom, map]);

  return null;
}

function Location({ userInfo }) {
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedStation, setSelectedStation] = useState(null);
  const [search, setSearch] = useState('');
  const [fdsData, setFdsData] = useState([]);
  const [fdsLoading, setFdsLoading] = useState(false);
  const [fdsError, setFdsError] = useState('');
  const [stationInfo, setStationInfo] = useState(null);
  const [allFdsStations, setAllFdsStations] = useState([]);
  const [filteredFdsStations, setFilteredFdsStations] = useState([]);
  const [fdsTableLoading, setFdsTableLoading] = useState(true);
  const [mapCenter, setMapCenter] = useState(null);
  const [mapZoom, setMapZoom] = useState(7);
  const [isMapExpanded, setIsMapExpanded] = useState(false);
  const mapRef = useRef(null);
  const [photoData, setPhotoData] = useState([]);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [currentDamageRecordIndex, setCurrentDamageRecordIndex] = useState(0);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedUploadedPhoto, setSelectedUploadedPhoto] = useState(null);

  // Notes state
  const [notesOpen, setNotesOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  // Notes for this station whose damage record no longer exists (orphaned by an FDS re-import)
  const [unlinkedNotes, setUnlinkedNotes] = useState([]);

  // @mention state
  const [mentionUsers, setMentionUsers] = useState([]);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionCursorPos, setMentionCursorPos] = useState(null);
  const noteInputRef = useRef(null);

  // Table sorting and filtering state
  const [sortColumn, setSortColumn] = useState('StationNo');
  const [sortDirection, setSortDirection] = useState('asc');
  const [stationFilter, setStationFilter] = useState('');
  const [descriptionFilter, setDescriptionFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');

  // "Has notes" filter — only show stations that have at least one note
  const [notesOnly, setNotesOnly] = useState(false);
  const [stationsWithNotes, setStationsWithNotes] = useState(new Set());

  useEffect(() => {
    fetchLocations();
    fetchAllFdsStations();
    fetchStationsWithNotes();
  }, []);

  // Resize the Leaflet map when toggling between inline and expanded views
  useEffect(() => {
    const timer = setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.invalidateSize();
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [isMapExpanded]);

  // Allow Escape to close the expanded map
  useEffect(() => {
    if (!isMapExpanded) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') setIsMapExpanded(false);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isMapExpanded]);

  // Ensure currentDamageRecordIndex stays within bounds when fdsData changes
  useEffect(() => {
    if (fdsData.length > 0 && currentDamageRecordIndex >= fdsData.length) {
      setCurrentDamageRecordIndex(0);
    }
  }, [fdsData, currentDamageRecordIndex]);

  // Effect for map locations filtering (search + notes-only)
  useEffect(() => {
    let filteredLocs = locations;
    if (search.trim() !== '') {
      const searchLower = search.toLowerCase();
      filteredLocs = filteredLocs.filter(location => {
        return (
          (location.STATION_NO && location.STATION_NO.toString().toLowerCase().includes(searchLower)) ||
          (location.Owner && location.Owner.toLowerCase().includes(searchLower)) ||
          (location.Description && location.Description.toLowerCase().includes(searchLower)) ||
          (location.StationType && location.StationType.toLowerCase().includes(searchLower)) ||
          (location.Route && location.Route.toLowerCase().includes(searchLower)) ||
          (location.Road && location.Road.toLowerCase().includes(searchLower)) ||
          (location.Stratum && location.Stratum.toString().toLowerCase().includes(searchLower))
        );
      });
    }
    if (notesOnly) {
      filteredLocs = filteredLocs.filter(location =>
        location.STATION_NO != null && stationsWithNotes.has(String(location.STATION_NO))
      );
    }
    setFilteredLocations(filteredLocs);
  }, [search, locations, notesOnly, stationsWithNotes]);

  // Separate effect for table filtering and sorting - Group by unique stations with record counts
  useEffect(() => {
    // Group damage records by station and count them
    const stationGroups = {};
    allFdsStations.forEach(record => {
      const stationNo = record.StationNo;
      if (!stationGroups[stationNo]) {
        stationGroups[stationNo] = {
          StationNo: stationNo,
          Description: record.Description,
          Owner: record.Owner,
          RecordCount: 0
        };
      }
      stationGroups[stationNo].RecordCount++;
    });

    // Convert to array
    let filtered = Object.values(stationGroups);

    // Apply global search filter
    if (search.trim() !== '') {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(station => {
        return (
          (station.StationNo && station.StationNo.toString().toLowerCase().includes(searchLower)) ||
          (station.Description && station.Description.toLowerCase().includes(searchLower)) ||
          (station.Owner && station.Owner.toLowerCase().includes(searchLower))
        );
      });
    }

    // Apply column filters
    if (stationFilter.trim() !== '') {
      filtered = filtered.filter(station =>
        station.StationNo && station.StationNo.toString().toLowerCase().includes(stationFilter.toLowerCase())
      );
    }
    if (descriptionFilter.trim() !== '') {
      filtered = filtered.filter(station =>
        station.Description && station.Description.toLowerCase().includes(descriptionFilter.toLowerCase())
      );
    }
    if (ownerFilter.trim() !== '') {
      filtered = filtered.filter(station =>
        station.Owner && station.Owner.toLowerCase().includes(ownerFilter.toLowerCase())
      );
    }

    // Apply "has notes" filter
    if (notesOnly) {
      filtered = filtered.filter(station =>
        station.StationNo != null && stationsWithNotes.has(String(station.StationNo))
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];

      // Handle RecordCount as number, others as string
      if (sortColumn === 'RecordCount') {
        aVal = aVal || 0;
        bVal = bVal || 0;
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
        if (sortDirection === 'asc') {
          return aVal.localeCompare(bVal);
        } else {
          return bVal.localeCompare(aVal);
        }
      }
    });

    setFilteredFdsStations(filtered);
  }, [search, allFdsStations, stationFilter, descriptionFilter, ownerFilter, notesOnly, stationsWithNotes, sortColumn, sortDirection]);

  const fetchLocations = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get('/api/locations/fds/map', { withCredentials: true });
      setLocations(response.data);
      setFilteredLocations(response.data);
      setError('');
    } catch (err) {
      setError('Failed to load location data');
      console.error('Locations error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllFdsStations = async () => {
    try {
      setFdsTableLoading(true);
      const response = await axios.get('/api/locations/fds/all', { withCredentials: true });
      setAllFdsStations(response.data);
      setFilteredFdsStations(response.data);
    } catch (err) {
      console.error('Error fetching all FDS stations:', err);
    } finally {
      setFdsTableLoading(false);
    }
  };

  const fetchStationsWithNotes = async () => {
    try {
      const response = await axios.get('/api/damage-notes/stations/with-notes', { withCredentials: true });
      setStationsWithNotes(new Set((response.data || []).map(String)));
    } catch (err) {
      console.error('Error fetching stations with notes:', err);
    }
  };

  const fetchFdsData = async (stationNo) => {
    try {
      setFdsLoading(true);
      setFdsError('');
      const response = await axios.get(`/api/locations/${stationNo}/fds`, { withCredentials: true });
      setFdsData(response.data);
    } catch (err) {
      setFdsError('Failed to load FDS data');
      console.error('FDS error:', err);
      setFdsData([]);
    } finally {
      setFdsLoading(false);
    }
  };

  const fetchPhotoData = async (stationNo) => {
    try {
      setPhotoLoading(true);
      console.log(`Fetching photos for station ${stationNo}...`);
      const response = await axios.get(`/api/locations/${stationNo}/damage-photos`, { withCredentials: true });
      console.log('Photo data received:', response.data);
      setPhotoData(response.data);
    } catch (err) {
      console.error('Photo data error:', err);
      setPhotoData([]);
    } finally {
      setPhotoLoading(false);
    }
  };

  const fetchUploadedPhotos = async (stationNo) => {
    try {
      const response = await axios.get(`/api/damage-photos/uploaded/${stationNo}`, { withCredentials: true });
      setUploadedPhotos(response.data);
    } catch (err) {
      console.error('Error fetching uploaded photos:', err);
      setUploadedPhotos([]);
    }
  };

  const deleteUploadedPhoto = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;
    try {
      await axios.delete(`/api/damage-photos/uploaded/${photoId}`, { withCredentials: true });
      setUploadedPhotos(prev => prev.filter(p => p.id !== photoId));
    } catch (err) {
      console.error('Error deleting photo:', err);
      alert('Failed to delete photo');
    }
  };

  const fetchNotes = async (fdsRecordId) => {
    try {
      setNotesLoading(true);
      const response = await axios.get(`/api/damage-notes/${fdsRecordId}`, { withCredentials: true });
      setNotes(response.data);
    } catch (err) {
      console.error('Error fetching notes:', err);
      setNotes([]);
    } finally {
      setNotesLoading(false);
    }
  };

  const saveNote = async () => {
    const currentRecord = fdsData[currentDamageRecordIndex];
    const fdsRecordId = currentRecord?.ID || currentRecord?.id;
    if (!fdsRecordId || !noteText.trim()) return;

    try {
      setNoteSaving(true);
      const response = await axios.post('/api/damage-notes', {
        fdsRecordId,
        stationNo: selectedStation,
        noteText: noteText.trim(),
      }, { withCredentials: true });
      setNotes(response.data);
      setNoteText('');
    } catch (err) {
      console.error('Error saving note:', err);
    } finally {
      setNoteSaving(false);
    }
  };

  const updateNote = async (noteId) => {
    if (!editingNoteText.trim()) return;
    try {
      const response = await axios.put(`/api/damage-notes/${noteId}`, {
        noteText: editingNoteText.trim(),
      }, { withCredentials: true });
      setNotes(response.data);
      setEditingNoteId(null);
      setEditingNoteText('');
    } catch (err) {
      console.error('Error updating note:', err);
    }
  };

  const deleteNote = async (noteId) => {
    try {
      const response = await axios.delete(`/api/damage-notes/${noteId}`, { withCredentials: true });
      setNotes(response.data);
    } catch (err) {
      console.error('Error deleting note:', err);
    }
  };

  // Fetch all notes for the station and keep only those whose record no longer exists
  const fetchUnlinkedNotes = async (stationNo, records) => {
    try {
      const res = await axios.get(`/api/damage-notes/by-station/${stationNo}`, { withCredentials: true });
      const currentIds = new Set((records || []).map(r => String(r.ID ?? r.id)));
      setUnlinkedNotes((res.data || []).filter(n => !currentIds.has(String(n.fds_record_id))));
    } catch (err) {
      console.error('Error fetching station notes:', err);
      setUnlinkedNotes([]);
    }
  };

  const deleteUnlinkedNote = async (noteId) => {
    try {
      await axios.delete(`/api/damage-notes/${noteId}`, { withCredentials: true });
      fetchUnlinkedNotes(selectedStation, fdsData);
      fetchStationsWithNotes(); // keep the "has notes" filter accurate after cleanup
    } catch (err) {
      console.error('Error deleting unlinked note:', err);
    }
  };

  // Fetch mentionable users on mount
  useEffect(() => {
    axios.get('/api/users/mentionable', { withCredentials: true })
      .then(res => setMentionUsers(res.data))
      .catch(() => {});
  }, []);

  // @mention handlers
  const handleNoteChange = (e) => {
    const val = e.target.value;
    setNoteText(val);

    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = val.slice(0, cursorPos);
    const mentionMatch = textBeforeCursor.match(/@(\S*)$/);

    if (mentionMatch) {
      setMentionFilter(mentionMatch[1].toLowerCase());
      setMentionCursorPos(mentionMatch.index);
      setShowMentionDropdown(true);
      setMentionIndex(0);
    } else {
      setShowMentionDropdown(false);
    }
  };

  const getFilteredMentionUsers = () => {
    return mentionUsers.filter(u => {
      const search = mentionFilter.toLowerCase();
      return u.username.toLowerCase().includes(search) ||
        (u.firstName && u.firstName.toLowerCase().includes(search)) ||
        (u.lastName && u.lastName.toLowerCase().includes(search));
    });
  };

  const insertMention = (user) => {
    const before = noteText.slice(0, mentionCursorPos);
    const afterCursor = noteInputRef.current?.selectionStart || (mentionCursorPos + mentionFilter.length + 1);
    const after = noteText.slice(afterCursor);
    const newText = before + '@' + user.username + ' ' + after;
    setNoteText(newText);
    setShowMentionDropdown(false);

    // Refocus textarea at correct position
    setTimeout(() => {
      const pos = before.length + user.username.length + 2; // @username + space
      if (noteInputRef.current) {
        noteInputRef.current.focus();
        noteInputRef.current.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const handleNoteKeyDown = (e) => {
    if (!showMentionDropdown) return;
    const filtered = getFilteredMentionUsers();
    if (filtered.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMentionIndex(prev => (prev + 1) % filtered.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMentionIndex(prev => (prev - 1 + filtered.length) % filtered.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      insertMention(filtered[mentionIndex]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setShowMentionDropdown(false);
    }
  };

  // Render note text with @mention highlighting
  const renderNoteText = (text) => {
    const parts = text.split(/(@\S+)/g);
    return parts.map((part, i) =>
      /^@\S+/.test(part) ? (
        <span key={i} className="inline-block bg-amber-100 text-amber-800 px-1 rounded text-xs font-medium">{part}</span>
      ) : (
        <span key={i}>{part}</span>
      )
    );
  };

  // Always fetch notes for the current record so we can show count
  useEffect(() => {
    if (fdsData.length > 0) {
      const currentRecord = fdsData[currentDamageRecordIndex];
      const fdsRecordId = currentRecord?.ID || currentRecord?.id;
      if (fdsRecordId) {
        fetchNotes(fdsRecordId);
      } else {
        setNotes([]);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDamageRecordIndex, fdsData]);

  // Recompute "unlinked" notes (station notes with no matching current record) on station change
  useEffect(() => {
    if (selectedStation) {
      fetchUnlinkedNotes(selectedStation, fdsData);
    } else {
      setUnlinkedNotes([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStation, fdsData]);

  const handleStationClick = (stationNo) => {
    setSelectedStation(stationNo);
    setCurrentDamageRecordIndex(0); // Reset to first damage record
    setNotesOpen(false);
    setNotes([]);
    setUnlinkedNotes([]);
    setNoteText('');
    fetchFdsData(stationNo);
    fetchPhotoData(stationNo);
    fetchUploadedPhotos(stationNo);

    // Find the station's coordinates and info, then zoom to it
    const station = locations.find(loc => loc.STATION_NO === stationNo);
    if (station) {
      setStationInfo(station);
      if (station.LAT && station.LONG) {
        const lat = parseFloat(station.LAT);
        const lng = parseFloat(station.LONG);
        if (!isNaN(lat) && !isNaN(lng)) {
          setMapCenter([lat, lng]);
          setMapZoom(12);
        }
      }
    }
  };

  const handleReset = () => {
    setSelectedStation(null);
    setStationInfo(null);
    setFdsData([]);
    setPhotoData([]);
    setCurrentDamageRecordIndex(0);
    setNotesOpen(false);
    setNotes([]);
    setUnlinkedNotes([]);
    setNoteText('');
    setMapCenter(getMapCenter());
    setMapZoom(7);

    // Clear search
    setSearch('');

    // Clear table filters
    setStationFilter('');
    setDescriptionFilter('');
    setOwnerFilter('');
    setNotesOnly(false);

    // Reset sorting
    setSortColumn('StationNo');
    setSortDirection('asc');
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const clearFilters = () => {
    setStationFilter('');
    setDescriptionFilter('');
    setOwnerFilter('');
  };

  // Calculate map center (average of all coordinates)
  const getMapCenter = () => {
    const dataToUse = filteredLocations.length > 0 ? filteredLocations : locations;
    if (dataToUse.length === 0) return [-28.4793, 24.6727]; // Default: South Africa center

    const avgLat = dataToUse.reduce((sum, loc) => sum + parseFloat(loc.LAT), 0) / dataToUse.length;
    const avgLong = dataToUse.reduce((sum, loc) => sum + parseFloat(loc.LONG), 0) / dataToUse.length;

    return [avgLat, avgLong];
  };

  // Create custom marker icon based on damage count
  const createDamageIcon = (damageCount, isSelected = false) => {
    // Scale marker size based on damage count (min 30px, max 60px)
    const baseSize = 30;
    const maxSize = 60;
    const size = Math.min(maxSize, baseSize + (damageCount * 3));

    // Light red color with transparency for fill, solid red for border
    const markerColor = 'rgba(255, 107, 107, 0.7)'; // 70% opacity
    const borderColor = '#ff6b6b'; // Solid red

    // eslint-disable-next-line no-unused-vars
    const animationStyle = isSelected ? `
      animation: pulse 1.5s ease-in-out infinite;
      @keyframes pulse {
        0%, 100% {
          transform: scale(1);
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        }
        50% {
          transform: scale(1.15);
          box-shadow: 0 4px 16px rgba(255, 107, 107, 0.8);
        }
      }
    ` : '';

    return L.divIcon({
      html: `
        <style>
          @keyframes pulse {
            0%, 100% {
              transform: scale(1);
              box-shadow: 0 2px 8px rgba(0,0,0,0.3);
            }
            50% {
              transform: scale(1.15);
              box-shadow: 0 4px 16px rgba(255, 107, 107, 0.8);
            }
          }
        </style>
        <div style="
          width: ${size}px;
          height: ${size}px;
          background-color: ${markerColor};
          border: 2px solid ${borderColor};
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: ${size > 40 ? '14px' : '12px'};
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          cursor: pointer;
          ${isSelected ? 'animation: pulse 1.5s ease-in-out infinite;' : ''}
        ">
          ${damageCount}
        </div>
      `,
      className: 'custom-damage-marker',
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
      popupAnchor: [0, -(size / 2)]
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading map...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        {error}
      </div>
    );
  }

  return (
    <div className="w-full overflow-hidden">
      {/* Search and Filters */}
      <div className="card p-3 mb-3">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          {/* Search */}
          <div className="md:col-span-6 lg:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-0.5">Search</label>
            <div className="relative">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search stations..."
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <svg className="absolute right-3 top-2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* Stats and Legend */}
          <div className="md:col-span-5 lg:col-span-3 flex flex-wrap gap-x-3 gap-y-1 items-center">
            <div className="text-xs text-gray-600">
              <span className="font-medium">Station Count:</span>
              <span className="ml-1 font-semibold text-red-600">{filteredFdsStations.length}</span>
            </div>
            <div className="text-xs text-gray-600">
              <span className="font-medium">Selected:</span>
              <span className="ml-1 font-semibold text-gray-900">{selectedStation || '-'}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-xs font-medium text-gray-600">Legend:</span>
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#ff6b6b' }}></div>
              <span className="text-xs text-gray-600">FDS (Size = Count)</span>
            </div>
          </div>

          {/* Notes filter + Reset Buttons */}
          <div className="md:col-span-1 lg:col-span-1 flex items-end justify-end gap-2">
            <button
              onClick={() => setNotesOnly(!notesOnly)}
              className={`w-8 h-8 flex items-center justify-center border-2 rounded-lg shadow-sm transition-all duration-200 ${
                notesOnly
                  ? 'bg-amber-500 border-amber-500 text-white hover:bg-amber-600'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
              title={notesOnly ? 'Showing only stations with notes — click to show all' : 'Show only stations that have notes'}
              aria-pressed={notesOnly}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </button>
            <button
              onClick={handleReset}
              className="w-8 h-8 flex items-center justify-center bg-white hover:bg-gray-50 border-2 border-gray-300 rounded-lg shadow-sm transition-all duration-200"
              title="Reset filters"
            >
              <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Map and FDS Table Row */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-6 mb-4 sm:mb-6">
        {/* Map Column - 40% width, reduced height */}
        <div className="lg:col-span-2 card p-0 overflow-hidden">
          {locations.length === 0 ? (
            <div className="p-12 text-center text-gray-600">
              No frequently damaged stations found. Stations with damage records in the FDS table will appear here.
            </div>
          ) : (
            <div
              className={
                isMapExpanded
                  ? 'fixed inset-0 z-[9999] bg-white p-3 sm:p-4 flex flex-col'
                  : 'h-[250px] sm:h-[350px] w-full relative'
              }
            >
            {isMapExpanded && (
              <div className="flex items-center justify-between mb-2 px-1">
                <h3 className="font-semibold text-gray-900 text-base">Frequently Damaged Stations Map</h3>
                <span className="text-xs text-gray-500">Press Esc to close</span>
              </div>
            )}
            <button
              onClick={() => setIsMapExpanded(!isMapExpanded)}
              className="absolute top-2 right-2 z-[1000] w-8 h-8 flex items-center justify-center bg-white hover:bg-gray-50 border-2 border-gray-300 rounded-lg shadow-md transition-all duration-200"
              title={isMapExpanded ? 'Close full-screen map' : 'Pop out map to full screen'}
            >
              {isMapExpanded ? (
                <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
              )}
            </button>
            <div className={isMapExpanded ? 'flex-1 w-full' : 'h-full w-full'}>
            <MapContainer
              ref={mapRef}
              center={mapCenter || getMapCenter()}
              zoom={mapZoom}
              style={{ height: '100%', width: '100%' }}
            >
              <MapUpdater center={mapCenter} zoom={mapZoom} />
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              {filteredLocations.map((location, index) => {
                const lat = parseFloat(location.LAT);
                const lng = parseFloat(location.LONG);
                const damageCount = location.DamageCount || 0;
                const isSelected = selectedStation === location.STATION_NO;

                if (isNaN(lat) || isNaN(lng)) return null;

                return (
                  <Marker
                    key={index}
                    position={[lat, lng]}
                    icon={createDamageIcon(damageCount, isSelected)}
                    eventHandlers={{
                      click: () => {
                        handleStationClick(location.STATION_NO);
                      },
                    }}
                  >
                    <Popup>
                      <div className="p-3 min-w-[250px]">
                        <p className="font-bold text-lg text-red-600 mb-2 border-b pb-2">
                          Station: {location.STATION_NO}
                        </p>
                        <div className="bg-red-50 border border-red-200 rounded px-3 py-2 mb-3">
                          <p className="font-bold text-red-700">
                            ⚠ Frequently Damaged Station
                          </p>
                          <p className="text-sm text-red-600 mt-1">
                            {damageCount} damage record{damageCount !== 1 ? 's' : ''}
                          </p>
                        </div>
                        <div className="space-y-1 text-sm">
                          {location.Owner && (
                            <p><span className="font-semibold">Owner:</span> {location.Owner}</p>
                          )}
                          {location.Description && (
                            <p><span className="font-semibold">Description:</span> {location.Description}</p>
                          )}
                          {location.StationType && (
                            <p><span className="font-semibold">Station Type:</span> {location.StationType}</p>
                          )}
                          {location.Route && (
                            <p><span className="font-semibold">Route:</span> {location.Route}</p>
                          )}
                          {location.Road && (
                            <p><span className="font-semibold">Road:</span> {location.Road}</p>
                          )}
                          {location.KM !== null && location.KM !== undefined && (
                            <p><span className="font-semibold">KM:</span> {location.KM}</p>
                          )}
                          {location.Stratum && (
                            <p><span className="font-semibold">Stratum:</span> {location.Stratum}</p>
                          )}
                          {location.Lanes !== null && location.Lanes !== undefined && (
                            <p><span className="font-semibold">Lanes:</span> {location.Lanes}</p>
                          )}
                          <div className="border-t pt-2 mt-2 text-xs text-gray-500">
                            <p>Lat: {lat.toFixed(6)}</p>
                            <p>Long: {lng.toFixed(6)}</p>
                          </div>
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
            </div>
          </div>
        )}
        </div>

        {/* FDS Table Column - 60% width */}
        <div className="lg:col-span-3 card h-[300px] sm:h-[350px] overflow-y-auto">
          <h3 className="font-semibold text-gray-900 mb-3 text-base">Frequently Damaged Stations</h3>

          {fdsTableLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-600">Loading FDS stations...</div>
            </div>
          ) : filteredFdsStations.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              {search ? 'No stations match your search.' : 'No frequently damaged stations found.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs text-gray-600">
                  Showing {filteredFdsStations.length} unique stations
                </span>
                {(stationFilter || descriptionFilter || ownerFilter) && (
                  <button
                    onClick={clearFilters}
                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    Clear all filters
                  </button>
                )}
              </div>
              <table className="min-w-[500px] w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th
                      className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('StationNo')}
                    >
                      <div className="flex items-center">
                        Station No
                        {sortColumn === 'StationNo' && (
                          <span className="ml-1">
                            {sortDirection === 'asc' ? '▲' : '▼'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('Description')}
                    >
                      <div className="flex items-center">
                        Description
                        {sortColumn === 'Description' && (
                          <span className="ml-1">
                            {sortDirection === 'asc' ? '▲' : '▼'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('Owner')}
                    >
                      <div className="flex items-center">
                        Owner
                        {sortColumn === 'Owner' && (
                          <span className="ml-1">
                            {sortDirection === 'asc' ? '▲' : '▼'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('RecordCount')}
                    >
                      <div className="flex items-center">
                        Record Count
                        {sortColumn === 'RecordCount' && (
                          <span className="ml-1">
                            {sortDirection === 'asc' ? '▲' : '▼'}
                          </span>
                        )}
                      </div>
                    </th>
                  </tr>
                  {/* Filter Row */}
                  <tr className="bg-gray-100">
                    <th className="px-3 py-1">
                      <input
                        type="text"
                        placeholder="Filter..."
                        value={stationFilter}
                        onChange={(e) => setStationFilter(e.target.value)}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </th>
                    <th className="px-3 py-1">
                      <input
                        type="text"
                        placeholder="Filter..."
                        value={descriptionFilter}
                        onChange={(e) => setDescriptionFilter(e.target.value)}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </th>
                    <th className="px-3 py-1">
                      <input
                        type="text"
                        placeholder="Filter..."
                        value={ownerFilter}
                        onChange={(e) => setOwnerFilter(e.target.value)}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </th>
                    <th className="px-3 py-1">
                      {/* No filter for Record Count */}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredFdsStations.map((station, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleStationClick(station.StationNo)}
                    >
                      <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                        {station.StationNo}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-900">
                        {station.Description || '-'}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {station.Owner || '-'}
                      </td>
                      <td className="px-3 py-2 text-sm text-center font-semibold text-gray-900">
                        {station.RecordCount}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Station Info & Damage History */}
      <div className="card">
        <h3 className="font-semibold text-gray-900 mb-4 text-base">Station Info & Damage History</h3>

        {!selectedStation ? (
          <div className="text-center text-gray-500 py-8">
            <p className="text-sm">Click a station on the map or in the table to view station information and damage history</p>
          </div>
        ) : fdsLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-gray-600">Loading station information...</div>
          </div>
        ) : fdsError ? (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {fdsError}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-4">
            {/* Column 1 - Station Info (3/12) */}
            <div className="sm:col-span-12 lg:col-span-3 flex">
              <div className="card bg-blue-50 border border-blue-200 flex-1">
                <div className="space-y-2 text-xs">
                  {/* Station No and Owner on same line */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <span className="font-medium text-blue-700">Station No:</span>
                      <p className="text-blue-900 font-semibold text-sm">{selectedStation}</p>
                    </div>
                    {stationInfo?.Owner && (
                      <div>
                        <span className="font-medium text-blue-700">Owner:</span>
                        <p className="text-blue-900">{stationInfo.Owner}</p>
                      </div>
                    )}
                  </div>

                  {/* Route and Road on same line */}
                  {(stationInfo?.Route || stationInfo?.Road) && (
                    <div className="grid grid-cols-2 gap-2">
                      {stationInfo?.Route && (
                        <div>
                          <span className="font-medium text-blue-700">Route:</span>
                          <p className="text-blue-900">{stationInfo.Route}</p>
                        </div>
                      )}
                      {stationInfo?.Road && (
                        <div>
                          <span className="font-medium text-blue-700">Road:</span>
                          <p className="text-blue-900">{stationInfo.Road}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Description on its own line */}
                  {stationInfo?.Description && (
                    <div>
                      <span className="font-medium text-blue-700">Description:</span>
                      <p className="text-blue-900">{stationInfo.Description}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Column 2 - Damage Records (3/12) */}
            <div className="sm:col-span-12 lg:col-span-3 flex">
              {fdsData.length === 0 ? (
                <div className="bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded-lg flex-1">
                  <p className="font-semibold text-xs">✓ No Damage Records</p>
                  <p className="text-xs mt-1">No damage records found.</p>
                </div>
              ) : (
                <div className="flex flex-col flex-1">
                  {/* Navigation Controls */}
                  <div className="flex items-center justify-between mb-2">
                    <button
                      onClick={() => setCurrentDamageRecordIndex(prev => Math.max(0, prev - 1))}
                      disabled={currentDamageRecordIndex === 0}
                      className="px-3 py-1 text-xs font-medium bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed rounded border border-gray-300 transition-colors"
                      title="Previous record"
                    >
                      ← Previous
                    </button>
                    <span className="text-xs font-semibold text-gray-700">
                      Record {currentDamageRecordIndex + 1} of {fdsData.length}
                    </span>
                    <button
                      onClick={() => setCurrentDamageRecordIndex(prev => Math.min(fdsData.length - 1, prev + 1))}
                      disabled={currentDamageRecordIndex === fdsData.length - 1}
                      className="px-3 py-1 text-xs font-medium bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed rounded border border-gray-300 transition-colors"
                      title="Next record"
                    >
                      Next →
                    </button>
                  </div>

                  {/* Display Current FDS Record */}
                  <div className="border border-gray-200 rounded p-2 bg-gray-50 flex-1">
                    <div className="space-y-1">
                      {Object.entries(fdsData[currentDamageRecordIndex]).map(([key, value]) => {
                        // Exclude redundant and system columns
                        if (key === 'RowNum' || key === 'ID' || key.toLowerCase() === 'id' ||
                            key === 'STATION NO.' || key.toLowerCase().includes('station') ||
                            key === 'Owner') return null;
                        return (
                          <div key={key} className="text-xs">
                            <span className="font-medium text-gray-600">{key}: </span>
                            <span className="text-gray-900">
                              {value !== null && value !== undefined ? String(value) : '-'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Column 3 - Damage Timeline (6/12) */}
            <div className="sm:col-span-12 lg:col-span-6 flex">
              {fdsData.length > 0 && (() => {
                // Helper function to format date as "Dec 2022"
                const formatDateShort = (dateStr) => {
                  const date = new Date(dateStr);
                  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                  return `${months[date.getMonth()]} ${date.getFullYear()}`;
                };

                // Process and format dates
                const records = fdsData
                  .map((record, index) => {
                    let date = record.DATE || record.Date || record.date;
                    if (date && typeof date === 'string' && date.includes('T')) {
                      date = date.split('T')[0];
                    }
                    return { ...record, formattedDate: date, originalIndex: index };
                  })
                  .filter(record => record.formattedDate)
                  .sort((a, b) => new Date(a.formattedDate) - new Date(b.formattedDate));

                if (records.length === 0) return null;

                const firstDate = new Date(records[0].formattedDate);
                const lastDate = new Date(records[records.length - 1].formattedDate);
                const totalDays = Math.max(1, (lastDate - firstDate) / (1000 * 60 * 60 * 24));

                return (
                  <div className="card bg-purple-50 border border-purple-200 flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-purple-900 text-sm">Damage Timeline</h4>
                      <div className="text-xs text-purple-800">
                        <span className="font-semibold">{records.length}</span> damage records from {formatDateShort(records[0].formattedDate)} to {formatDateShort(records[records.length - 1].formattedDate)}
                      </div>
                    </div>

                    {/* Horizontal Timeline - Reduced Height */}
                    <div className="relative pt-6 pb-14 px-2 overflow-x-auto" style={{ minWidth: 0 }}>
                      {/* Timeline line */}
                      <div className="absolute top-6 left-2 right-2 h-0.5 bg-purple-300"></div>

                      {/* Damage incidents with dates */}
                      {(() => {
                        // Calculate positions and detect overlaps - constrained to 0-100% within padded area
                        const positions = records.map((record, index) => {
                          const recordDate = new Date(record.formattedDate);
                          const daysFromStart = (recordDate - firstDate) / (1000 * 60 * 60 * 24);
                          const position = totalDays > 0 ? (daysFromStart / totalDays) * 100 : 50;
                          return { ...record, position };
                        });

                        // Assign horizontal offset levels to avoid overlaps (for vertical text)
                        const threshold = 0.5; // If positions are within 0.5% of each other, stagger them horizontally
                        positions.forEach((item, index) => {
                          if (index === 0) {
                            item.level = 0;
                          } else {
                            // Check if close to previous items
                            let level = 0;
                            const usedLevels = new Set();

                            for (let i = index - 1; i >= 0; i--) {
                              const distance = Math.abs(item.position - positions[i].position);
                              if (distance < threshold) {
                                usedLevels.add(positions[i].level);
                              }
                            }

                            // Find first available level
                            while (usedLevels.has(level)) {
                              level++;
                            }
                            item.level = level;
                          }
                        });

                        return positions.map((item) => {
                          // Horizontal offset in pixels for staggering side-by-side (for vertical labels)
                          const horizontalOffset = item.level * 15; // 15px apart
                          const isActive = item.originalIndex === currentDamageRecordIndex;

                          return (
                            <div
                              key={item.originalIndex}
                              className="absolute top-6 transform -translate-y-1/2 group"
                              style={{ left: `calc(${item.position}% + ${horizontalOffset}px)` }}
                            >
                              {/* Pulsating ring for active record */}
                              {isActive && (
                                <div
                                  className="absolute rounded-full z-[5]"
                                  style={{
                                    width: '24px',
                                    height: '24px',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    backgroundColor: 'rgba(239, 68, 68, 0.3)',
                                    animation: 'timeline-pulse 1.5s ease-in-out infinite',
                                  }}
                                />
                              )}
                              {/* Dot */}
                              <div
                                className={`rounded-full border-2 border-white shadow-md cursor-pointer z-10 relative transition-all duration-200 ${
                                  isActive
                                    ? 'w-5 h-5 bg-red-600 -ml-1 -mt-1'
                                    : 'w-3 h-3 bg-red-500 hover:scale-150'
                                }`}
                                onClick={() => setCurrentDamageRecordIndex(item.originalIndex)}
                              ></div>

                              {/* Date label - completely vertical with year at bottom near marker */}
                              <p className={`absolute top-4 left-1/2 whitespace-nowrap mt-1 origin-bottom ${
                                isActive
                                  ? 'text-[11px] font-semibold text-purple-900'
                                  : 'text-[10px] text-purple-700'
                              }`} style={{ transform: 'translateX(-50%) rotate(-90deg)' }}>
                                {formatDateShort(item.formattedDate)}
                              </p>

                              {/* Tooltip on hover */}
                              <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-white border border-purple-300 rounded shadow-lg p-3 z-20 pointer-events-none min-w-[250px]">
                                <p className="text-xs font-semibold text-purple-900 mb-2 border-b border-purple-200 pb-1">Record #{item.originalIndex + 1}</p>
                                <div className="space-y-1">
                                  {item['Damage Type'] && (
                                    <div className="text-xs">
                                      <span className="font-semibold text-gray-700">Damage Type:</span>
                                      <p className="text-gray-600 ml-2">{item['Damage Type']}</p>
                                    </div>
                                  )}
                                  {item['Affected Component(s)'] && (
                                    <div className="text-xs">
                                      <span className="font-semibold text-gray-700">Affected Components:</span>
                                      <p className="text-gray-600 ml-2">{item['Affected Component(s)']}</p>
                                    </div>
                                  )}
                                  {item['Remedial Action'] && (
                                    <div className="text-xs">
                                      <span className="font-semibold text-gray-700">Remedial Action:</span>
                                      <p className="text-gray-600 ml-2">{item['Remedial Action']}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Notes sub-heading (full width, between cards and photos) */}
            {selectedStation && fdsData.length > 0 && (
              <div className="sm:col-span-12 mt-4">
                {/* Collapsible header */}
                <button
                  onClick={() => setNotesOpen(prev => !prev)}
                  className="w-full flex items-center gap-2 py-2 px-1 text-left group"
                >
                  <span className={`flex items-center justify-center w-5 h-5 rounded text-xs font-bold transition-colors ${
                    notesOpen
                      ? 'bg-amber-500 text-white'
                      : 'bg-gray-200 text-gray-600 group-hover:bg-amber-100 group-hover:text-amber-700'
                  }`}>
                    {notesOpen ? '−' : '+'}
                  </span>
                  <span className="text-sm font-semibold text-gray-700">Notes</span>
                  {notes.length > 0 && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700">
                      {notes.length}
                    </span>
                  )}
                  <span className="text-xs text-gray-400 ml-1">
                    Record {currentDamageRecordIndex + 1} ({fdsData[currentDamageRecordIndex]?.ID || fdsData[currentDamageRecordIndex]?.id})
                  </span>
                </button>

                {/* Expanded notes content */}
                {notesOpen && (
                  <div className="card bg-amber-50 border border-amber-200 mt-1">
                    {/* Existing notes */}
                    <div className="mb-3 max-h-48 overflow-y-auto">
                      {notesLoading ? (
                        <p className="text-xs text-gray-500">Loading notes...</p>
                      ) : notes.length === 0 ? (
                        <p className="text-xs text-gray-500 italic">No notes yet for this record.</p>
                      ) : (
                        <div className="space-y-2">
                          {notes.map((note) => (
                            <div key={note.id} className="bg-white border border-amber-200 rounded p-2">
                              {editingNoteId === note.id ? (
                                <div>
                                  <textarea
                                    value={editingNoteText}
                                    onChange={(e) => setEditingNoteText(e.target.value)}
                                    rows={2}
                                    className="w-full px-2 py-1.5 text-sm border border-amber-300 rounded focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                                    autoFocus
                                  />
                                  <div className="flex gap-1.5 mt-1.5">
                                    <button
                                      onClick={() => updateNote(note.id)}
                                      disabled={!editingNoteText.trim()}
                                      className="px-2.5 py-1 text-xs font-medium bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-40 transition-colors"
                                    >
                                      Save
                                    </button>
                                    <button
                                      onClick={() => { setEditingNoteId(null); setEditingNoteText(''); }}
                                      className="px-2.5 py-1 text-xs font-medium text-gray-600 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{renderNoteText(note.note_text)}</p>
                                  <div className="flex items-center justify-between mt-1.5">
                                    <div className="flex items-center gap-2 text-[10px] text-gray-400">
                                      <span className="font-medium">{note.created_by}</span>
                                      <span>—</span>
                                      <span>{new Date(note.created_at).toLocaleString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <button
                                        onClick={() => { setEditingNoteId(note.id); setEditingNoteText(note.note_text); }}
                                        className="p-1 text-gray-400 hover:text-amber-600 rounded transition-colors"
                                        title="Edit note"
                                      >
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                        </svg>
                                      </button>
                                      <button
                                        onClick={() => { if (window.confirm('Delete this note?')) deleteNote(note.id); }}
                                        className="p-1 text-gray-400 hover:text-red-600 rounded transition-colors"
                                        title="Delete note"
                                      >
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Add note input */}
                    <div className="flex gap-2">
                      <div className="flex-1 relative">
                        <textarea
                          ref={noteInputRef}
                          value={noteText}
                          onChange={handleNoteChange}
                          onKeyDown={handleNoteKeyDown}
                          placeholder="Add a note... (type @ to mention someone)"
                          rows={2}
                          className="w-full px-3 py-2 text-sm border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                        />
                        {showMentionDropdown && getFilteredMentionUsers().length > 0 && (
                          <div className="absolute bottom-full left-0 mb-1 w-64 max-h-40 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                            {getFilteredMentionUsers().map((user, idx) => (
                              <div
                                key={user.username}
                                className={`px-3 py-2 cursor-pointer text-sm flex items-center gap-2 ${idx === mentionIndex ? 'bg-amber-50 text-amber-900' : 'hover:bg-gray-50 text-gray-700'}`}
                                onMouseDown={(e) => { e.preventDefault(); insertMention(user); }}
                                onMouseEnter={() => setMentionIndex(idx)}
                              >
                                <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                                  {(user.firstName?.[0] || user.username[0]).toUpperCase()}
                                </span>
                                <span className="font-medium">@{user.username}</span>
                                {user.firstName && <span className="text-gray-400 text-xs">{user.firstName} {user.lastName || ''}</span>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={saveNote}
                        disabled={!noteText.trim() || noteSaving}
                        className="px-4 py-2 text-sm font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors self-end"
                      >
                        {noteSaving ? 'Saving...' : 'Add Note'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Unlinked notes — captured against a damage record that no longer exists (e.g. after an FDS re-import) */}
            {selectedStation && unlinkedNotes.length > 0 && (
              <div className="sm:col-span-12 mt-3">
                <div className="card bg-orange-50 border border-orange-200">
                  <div className="flex items-center gap-2 mb-2">
                    <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <span className="text-sm font-semibold text-orange-800">Other notes for this station</span>
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-orange-100 text-orange-700">
                      {unlinkedNotes.length}
                    </span>
                  </div>
                  <p className="text-[11px] text-orange-700 mb-2">
                    These notes were captured against a damage record that is no longer present (the record may have changed on a data re-import). They aren't tied to any of the current records above.
                  </p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {unlinkedNotes.map((note) => (
                      <div key={note.id} className="bg-white border border-orange-200 rounded p-2">
                        <p className="text-sm text-gray-900 whitespace-pre-wrap">{renderNoteText(note.note_text)}</p>
                        <div className="flex items-center justify-between mt-1.5">
                          <div className="flex items-center gap-2 text-[10px] text-gray-400">
                            <span className="font-medium">{note.created_by}</span>
                            <span>—</span>
                            <span>{new Date(note.created_at).toLocaleString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                            <span className="text-orange-400">· was record {note.fds_record_id}</span>
                          </div>
                          <button
                            onClick={() => { if (window.confirm('Delete this note?')) deleteUnlinkedNote(note.id); }}
                            className="p-1 text-gray-400 hover:text-red-600 rounded transition-colors"
                            title="Delete note"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Column 4 - Station Photos (full width below timeline) */}
            {selectedStation && (
              <div className="sm:col-span-12 mt-4">
                <div className="card bg-green-50 border border-green-200">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-green-900 text-sm">Damage Photos</h4>
                    <button
                      onClick={() => setShowUploadModal(true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                      Upload Photo
                    </button>
                  </div>
                  {photoLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="text-gray-600">Loading photos...</div>
                    </div>
                  ) : (
                    <>
                      <PhotoGallery photos={photoData} />

                      {/* Uploaded Photos */}
                      {uploadedPhotos.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-green-200">
                          <h5 className="text-xs font-semibold text-green-800 mb-3">Uploaded Photos</h5>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                            {uploadedPhotos.map((photo) => (
                              <div key={photo.id} className="group relative">
                                <div
                                  className="aspect-video bg-gray-100 rounded-lg overflow-hidden border border-gray-200 hover:border-blue-500 transition-all hover:shadow-lg cursor-pointer"
                                  onClick={() => setSelectedUploadedPhoto(photo)}
                                >
                                  <img
                                    src={photo.blob_url}
                                    alt={photo.label || 'Uploaded photo'}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23f0f0f0" width="100" height="100"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="14"%3ENo Image%3C/text%3E%3C/svg%3E';
                                    }}
                                  />
                                </div>
                                <p className="text-xs text-gray-600 mt-2 text-center">{photo.label || 'No label'}</p>
                                <p className="text-[10px] text-gray-400 text-center">
                                  {photo.uploaded_by} — {new Date(photo.uploaded_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </p>
                                {/* Delete button */}
                                <button
                                  onClick={(e) => { e.stopPropagation(); deleteUploadedPhoto(photo.id); }}
                                  className="absolute top-1 right-1 bg-white bg-opacity-80 hover:bg-red-100 text-red-500 rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="Delete photo"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            ))}
                          </div>

                          {/* Uploaded Photo Lightbox */}
                          {selectedUploadedPhoto && (
                            <div
                              className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[9999] p-4"
                              onClick={() => setSelectedUploadedPhoto(null)}
                            >
                              <div
                                className="relative max-w-6xl max-h-full bg-white rounded-lg overflow-hidden"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  onClick={() => setSelectedUploadedPhoto(null)}
                                  className="absolute top-4 right-4 bg-white text-gray-800 rounded-full w-10 h-10 flex items-center justify-center hover:bg-gray-200 transition-colors shadow-lg z-10"
                                  aria-label="Close"
                                >
                                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                                <div className="absolute top-4 left-4 bg-white bg-opacity-90 px-4 py-2 rounded shadow-lg z-10">
                                  <p className="font-semibold text-gray-800">{selectedUploadedPhoto.label || 'Uploaded Photo'}</p>
                                  <p className="text-xs text-gray-500">
                                    {selectedUploadedPhoto.uploaded_by} — {new Date(selectedUploadedPhoto.uploaded_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}
                                  </p>
                                </div>
                                <img
                                  src={selectedUploadedPhoto.blob_url}
                                  alt={selectedUploadedPhoto.label || 'Uploaded photo'}
                                  className="max-h-[90vh] w-auto mx-auto"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23f0f0f0" width="100" height="100"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="14"%3ENo Image%3C/text%3E%3C/svg%3E';
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Photo Upload Modal */}
            <PhotoUploadModal
              isOpen={showUploadModal}
              onClose={() => setShowUploadModal(false)}
              stationNo={selectedStation}
              damageRecords={fdsData}
              currentRecordIndex={currentDamageRecordIndex}
              onUploadSuccess={() => fetchUploadedPhotos(selectedStation)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default Location;
